// src/hooks/useStripe.ts
import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { message } from 'antd';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

export const useStripe = () => {
  const [loading, setLoading] = useState(false);

  // planName accepts a plan key/alias ('agent'|'standard'|'team'|'pro');
  // the backend maps it to the base + metered-overage Stripe prices.
  const createCheckoutSession = async (planName: string) => {
    setLoading(true);

    try {
      // Get current user token
      const { auth } = await import('../utils/firebase');
      const token = await auth.currentUser?.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_name: planName,
        }),
      });

      const data = await response.json().catch(() => ({} as any));

      if (!response.ok) {
        throw new Error(data?.error || `Checkout failed (${response.status})`);
      }

      if (data?.session_id) {
        const stripe = await stripePromise;
        await stripe?.redirectToCheckout({ sessionId: data.session_id });
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      message.error(error instanceof Error ? error.message : 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const createCustomerPortalSession = async () => {
    setLoading(true);
    
    try {
      const { auth } = await import('../utils/firebase');
      const token = await auth.currentUser?.getIdToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const { url } = await response.json();
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Customer portal error:', error);
      message.error('Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  return {
    createCheckoutSession,
    createCustomerPortalSession,
    loading,
  };
};